/**
 * Copyright (c) 2025 Stefan Radig
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

export interface DebugEvent {
    timestamp: number;
    relativeTime: number; // ms since start
    type: 'send-start' | 'send-response' | 'send-complete' | 'send-gap' | 'receive' | 'poll' | 'apply-start' | 'apply-end' | 'error' | 'state' | 'init' | 'network' | 'offline' | 'storage' | 'compact' | 'csp';
    message: string;
    data?: any;
}

/** Public interface for DebugLogger */
export interface IDebugLogger {
    log(type: DebugEvent['type'], message: string, data?: any): void;
    /** Set the source identifier for shell debug log (e.g. plugin type). Defaults to 'core'. */
    setSource(source: string): void;
    getEvents(): DebugEvent[];
    clear(): void;
    getFormattedLog(): string;
    getSummary(): string;
    enable(): void;
    disable(): void;
}

class DebugLoggerClass implements IDebugLogger {
    private events: DebugEvent[] = [];
    private startTime: number = Date.now();
    private maxEvents: number = 1000; // Keep last 1000 events
    private enabled: boolean = false;
    private source: string = 'core';

    constructor() {
        // Restore enabled state from localStorage
        try {
            this.enabled = localStorage.getItem('debugLoggerEnabled') === 'true';
        } catch (e) {
            // localStorage might be unavailable
        }
    }

    log(type: DebugEvent['type'], message: string, data?: any): void {
        // Always forward to shell for centralized debug log (right-click / long-press the Editoor logo → "Debug logs")
        try {
            if (typeof window !== 'undefined' && window.parent && window.parent !== window) {
                window.parent.postMessage({
                    type: 'shell:debugLog',
                    payload: { source: this.source, type, message, data }
                }, window.location.origin);
            }
        } catch (e) {
            // Cross-origin or unavailable - ignore
        }

        if (!this.enabled) return;

        const now = Date.now();
        const event: DebugEvent = {
            timestamp: now,
            relativeTime: now - this.startTime,
            type,
            message,
            data
        };

        this.events.push(event);

        // Keep only last maxEvents
        if (this.events.length > this.maxEvents) {
            this.events.shift();
        }

        // Also log to console for real-time monitoring
        const prefix = `[DEBUG ${event.relativeTime}ms]`;
        switch (type) {
            case 'error':
                console.error(prefix, message, data);
                break;
            case 'send-gap':
                console.warn(prefix, message, data);
                break;
            default:
                console.log(prefix, message, data);
        }
    }

    getEvents(): DebugEvent[] {
        return [...this.events];
    }

    clear(): void {
        this.events = [];
        this.startTime = Date.now();
    }

    getFormattedLog(): string {
        const lines: string[] = [];
        lines.push('=== Debug Log ===');
        lines.push(`Session Start: ${new Date(this.startTime).toISOString()}`);
        lines.push(`Total Events: ${this.events.length}`);
        lines.push('');

        this.events.forEach(event => {
            const time = `[${event.relativeTime.toString().padStart(8, ' ')}ms]`;
            const type = `[${event.type.padEnd(15, ' ')}]`;
            const data = event.data ? ` | ${JSON.stringify(event.data)}` : '';
            lines.push(`${time} ${type} ${event.message}${data}`);
        });

        return lines.join('\n');
    }

    getSummary(): string {
        const summary: any = {
            totalEvents: this.events.length,
            byType: {} as Record<string, number>,
            errors: this.events.filter(e => e.type === 'error').length,
            gaps: this.events.filter(e => e.type === 'send-gap').length,
            sends: this.events.filter(e => e.type === 'send-complete').length,
            receives: this.events.filter(e => e.type === 'receive').length,
        };

        this.events.forEach(event => {
            summary.byType[event.type] = (summary.byType[event.type] || 0) + 1;
        });

        return JSON.stringify(summary, null, 2);
    }

    enable(): void {
        this.enabled = true;
        try {
            localStorage.setItem('debugLoggerEnabled', 'true');
        } catch (e) {}
        console.log('[DebugLogger] Enabled - reload to see init logs');
    }

    disable(): void {
        this.enabled = false;
        try {
            localStorage.removeItem('debugLoggerEnabled');
        } catch (e) {}
        console.log('[DebugLogger] Disabled');
    }

    setSource(source: string): void {
        this.source = source;
    }
}

// Singleton instance (exported with interface type for declaration files)
export const DebugLogger: IDebugLogger = new DebugLoggerClass();

/**
 * Report Content-Security-Policy violations to the debug log. A script, request,
 * embed or worker the policy blocks otherwise fails silently in the apps, where no
 * browser console is visible. Logs the offending resource and directive, never the
 * page URL (its fragment is the document key).
 */
export function captureCspViolations(logger: IDebugLogger, target: Pick<Document, 'addEventListener'> | undefined): void {
    if (!target || typeof target.addEventListener !== 'function') return;
    target.addEventListener('securitypolicyviolation', (event: SecurityPolicyViolationEvent) => {
        const blocked = event.blockedURI || '(inline)';
        logger.log('csp', `CSP blocked ${event.effectiveDirective || event.violatedDirective}: ${blocked}`, {
            directive: event.effectiveDirective || event.violatedDirective,
            blocked,
            source: event.sourceFile ? `${event.sourceFile}:${event.lineNumber}:${event.columnNumber}` : undefined,
            disposition: event.disposition,
        });
    });
}

captureCspViolations(DebugLogger, typeof document !== 'undefined' ? document : undefined);

// Expose to window for console access
if (typeof window !== 'undefined') {
    (window as any).DebugLogger = DebugLogger;
}
